import { CartAddEvent } from '@theme/events';
import { onAnimationEnd, yieldToMainThread } from '@theme/utilities';
import { formatMoney } from '@theme/money-formatting';

const FALLBACK_LIMIT = 3;
const RECOMMENDATIONS_REQUEST_LIMIT = 10;
const ADDED_HOLD_DURATION = 800;

/**
 * Collects section IDs from all cart-items-component elements on the page.
 * @returns {string} Comma-separated section IDs
 */
function getCartSectionIds() {
  const ids = new Set();
  document.querySelectorAll('cart-items-component').forEach((el) => {
    if (el instanceof HTMLElement && el.dataset.sectionId) {
      ids.add(el.dataset.sectionId);
    }
  });
  return Array.from(ids).join(',');
}

/**
 * Resolves the cart's new total item count after an add.
 * @param {Record<string, string>|undefined} sections - Rendered sections from the cart/add response.
 * @returns {number}
 */
function getNewCartCount(sections) {
  if (sections) {
    for (const html of Object.values(sections)) {
      const text = new DOMParser()
        .parseFromString(html, 'text/html')
        .querySelector('[ref="cartItemCount"]')
        ?.textContent?.trim();
      if (text) return parseInt(text, 10);
    }
  }

  const current = parseInt(document.querySelector('[ref="cartBubbleCount"]')?.textContent ?? '0', 10);
  return (Number.isNaN(current) ? 0 : current) + 1;
}

/**
 * @param {*} str
 */
function escapeHtml(str) {
  return String(str ?? '').replace(/[&<>"']/g, (c) => {
    const map = { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' };
    return map[/** @type {keyof typeof map} */ (c)] || c;
  });
}

/**
 * Build the same row markup that cart-upsell-row.liquid produces, from a
 * Recommendations-API product object.
 * @param {*} product
 * @param {object} options
 * @param {string} options.addToCartLabel
 * @param {string} options.thumbnailStyle - Cart thumbnail border custom properties
 * @param {{ added: string, error: string }} options.icons - Checkmark burst and error icon SVGs
 * @param {string} options.moneyFormat - The shop's money format
 * @param {string} options.currency - ISO currency code
 */
function buildUpsellRow(product, { addToCartLabel, thumbnailStyle, icons, moneyFormat, currency }) {
  const variants = /** @type {Array<*>} */ (product.variants || []);
  const availableVariants = variants.filter((v) => v.available);
  const variant = availableVariants[0] || variants[0];
  if (!variant) return '';

  const price = Number(variant.price);
  const compareAt = Number(variant.compare_at_price);
  const onSale = compareAt > price;

  const imageHtml = product.featured_image
    ? `<a href="${escapeHtml(product.url)}" class="cart-items__media-container" style="--ratio:1;">
         <img class="cart-items__media-image border-style" style="${escapeHtml(thumbnailStyle)}" src="${escapeHtml(product.featured_image)}&width=250" alt="${escapeHtml(product.title)}" loading="lazy" />
       </a>`
    : '';

  let variantSelectHtml = '';
  if (availableVariants.length > 1) {
    const options = availableVariants
      .map(
        (v) =>
          `<option value="${v.id}" data-price="${v.price}" ${v.id === variant.id ? 'selected' : ''}>${escapeHtml(v.title)}</option>`
      )
      .join('');
    variantSelectHtml = `
      <div class="upsell-variant-selector">
        <label for="upsell-variant-${product.id}" class="visually-hidden">Variants</label>
        <select id="upsell-variant-${product.id}" class="upsell-variant-select" data-product-id="${product.id}">
          ${options}
        </select>
      </div>
    `;
  }

  const priceHtml = onSale
    ? `<span>${formatMoney(price, moneyFormat, currency)}</span> <s class="compare-at-price">${formatMoney(compareAt, moneyFormat, currency)}</s>`
    : `<span>${formatMoney(price, moneyFormat, currency)}</span>`;

  return `
    <tr role="row" class="cart-items__table-row cart-items__table-row--full-width-variants upsell-product-row" data-product-id="${product.id}">
      <td class="cart-items__media" role="cell" headers="upsellProductImage">${imageHtml}</td>
      <td class="cart-items__details cart-primary-typography" role="cell" headers="upsellProductInformation">
        <div class="cart-items__product-info">
          <div class="cart-items__title-row">
            <p><a href="${escapeHtml(product.url)}" class="cart-items__title">${escapeHtml(product.title)}</a></p>
            <div class="cart-items__unit-price-wrapper"><div>${priceHtml}</div></div>
          </div>
        </div>
        <div class="cart-items__variants-wrapper">${variantSelectHtml}</div>
      </td>
      <td class="cart-items__quantity" role="cell" headers="upsellQuantity">
        <button type="button" class="upsell-add-btn add-to-cart-button" data-variant-id="${variant.id}" data-product-id="${product.id}" data-product-title="${escapeHtml(product.title)}">
          <span class="add-to-cart-text">${escapeHtml(addToCartLabel)}</span>
          <span class="add-to-cart__added"><span class="svg-wrapper add-to-cart__added-icon">${icons.added}</span></span>
        </button>
      </td>
      <td class="cart-items__error hidden" role="cell">
        <div class="cart-item__error" role="alert">
          <span class="svg-wrapper">${icons.error}</span>
          <small class="cart-item__error-text cart-primary-typography"></small>
        </div>
      </td>
    </tr>
  `;
}

const fallbackLoading = new WeakSet();

/** @type {Map<string, Array<*>>} */
const recommendationsByAnchor = new Map();

/**
 * Populate a fallback wrapper by fetching from the Shopify Recommendations API.
 * @param {HTMLElement} wrapper
 */
async function populateFallback(wrapper) {
  if (!(wrapper instanceof HTMLElement)) return;
  if (fallbackLoading.has(wrapper)) return;
  const target = wrapper.querySelector('[data-cart-upsell-rows]');
  if (!target || target.children.length > 0) return;

  const productId = wrapper.dataset.anchorProductId;
  if (!productId) return;
  const shopify = /** @type {*} */ (window.Shopify);
  const rowOptions = {
    addToCartLabel: wrapper.dataset.addToCartLabel || 'Add to cart',
    thumbnailStyle: wrapper.dataset.thumbnailStyle || '',
    icons: {
      added: wrapper.querySelector('[data-upsell-added-icon]')?.innerHTML ?? '',
      error: wrapper.querySelector('[data-upsell-error-icon]')?.innerHTML ?? '',
    },
    moneyFormat: wrapper.dataset.moneyFormat || '${{amount}}',
    currency: shopify?.currency?.active || 'AUD',
  };
  const cartProductIds = (wrapper.dataset.cartProductIds || '').split(',');

  fallbackLoading.add(wrapper);
  try {
    let products = recommendationsByAnchor.get(productId);
    if (!products) {
      const root = (shopify && shopify.routes && shopify.routes.root) || '/';
      const res = await fetch(
        `${root}recommendations/products.json?product_id=${productId}&intent=complementary&limit=${RECOMMENDATIONS_REQUEST_LIMIT}`,
        { headers: { Accept: 'application/json' } }
      );
      if (!res.ok) return;
      const data = await res.json();
      products = /** @type {Array<*>} */ (data.products || []).filter((/** @type {*} */ p) => p.available);
      recommendationsByAnchor.set(productId, products);
    }
    const rows = products
      .filter((/** @type {*} */ p) => !cartProductIds.includes(String(p.id)))
      .slice(0, FALLBACK_LIMIT);
    if (rows.length === 0) return;
    const html = rows.map((/** @type {*} */ p) => buildUpsellRow(p, rowOptions)).join('');
    target.insertAdjacentHTML('beforeend', html);
  } catch (err) {
    console.error('Cart upsell fallback error:', err);
  } finally {
    fallbackLoading.delete(wrapper);
  }
}

/**
 * @param {Document | HTMLElement} [root]
 */
function scanForFallbacks(root) {
  const scope = root || document;
  scope.querySelectorAll('[data-cart-upsell-fallback]').forEach((el) => {
    if (el instanceof HTMLElement) populateFallback(el);
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => scanForFallbacks());
} else {
  scanForFallbacks();
}

// Cart morphing replaces nodes or empties the fallback rows — refill whenever either happens.
const observer = new MutationObserver((mutations) => {
  for (const m of mutations) {
    const changedWrapper = m.target instanceof Element ? m.target.closest('[data-cart-upsell-fallback]') : null;
    if (changedWrapper instanceof HTMLElement) populateFallback(changedWrapper);

    for (const node of m.addedNodes) {
      if (!(node instanceof HTMLElement)) continue;
      if (node.matches('[data-cart-upsell-fallback]')) {
        populateFallback(node);
      } else if (node.querySelector('[data-cart-upsell-fallback]')) {
        scanForFallbacks(node);
      }
    }
  }
});
observer.observe(document.body, { childList: true, subtree: true });

/**
 * Plays the theme's add-to-cart checkmark burst and resolves once it has finished.
 * @param {HTMLElement} btn
 */
async function playAddedAnimation(btn) {
  btn.dataset.added = 'true';
  await yieldToMainThread();
  await onAnimationEnd(btn);
  await new Promise((resolve) => setTimeout(resolve, ADDED_HOLD_DURATION));
}

/**
 * @param {Element|null|undefined} row
 * @param {string} message
 */
function setRowError(row, message) {
  const container = row?.querySelector('.cart-items__error');
  const text = container?.querySelector('.cart-item__error-text');
  if (!container || !text) return;
  text.textContent = message;
  container.classList.toggle('hidden', !message);
}

/**
 * Brings the cart line the upsell landed on into view.
 * @param {Element|null} cartItems - The cart-items-component the upsell was added from.
 * @param {string|undefined} lineKey
 */
function revealCartLine(cartItems, lineKey) {
  if (!lineKey) return;
  const line = (cartItems ?? document).querySelector(`[data-key="${CSS.escape(lineKey)}"]`);
  line?.scrollIntoView({ behavior: 'instant', block: 'nearest' });
}

// Handle ATC button clicks via event delegation (covers both SSR and JS-injected rows).
document.addEventListener('click', async (event) => {
  const btn = /** @type {HTMLElement|null} */ (event.target?.closest('.upsell-add-btn'));
  if (!btn || btn.dataset.added === 'true') return;

  const row = btn.closest('.upsell-product-row');
  const variantSelect = /** @type {HTMLSelectElement|null} */ (row?.querySelector('.upsell-variant-select'));
  const variantId = variantSelect?.value || btn.dataset.variantId;

  if (!variantId) return;

  const cartItems = btn.closest('cart-items-component');
  setRowError(row, '');
  const addedAnimation = playAddedAnimation(btn);

  const formData = new FormData();
  formData.set('id', variantId);
  formData.set('quantity', '1');

  const sections = getCartSectionIds();
  if (sections) formData.set('sections', sections);

  try {
    const res = await fetch(Theme.routes.cart_add_url, {
      method: 'POST',
      headers: { 'X-Requested-With': 'XMLHttpRequest' },
      body: formData,
    });
    const data = await res.json();

    if (data.status) {
      btn.removeAttribute('data-added');
      setRowError(row, data.description || data.message || '');
      return;
    }

    await addedAnimation;

    btn.dispatchEvent(
      new CartAddEvent(data, variantId, {
        source: 'cart-upsell',
        itemCount: getNewCartCount(data.sections),
        variantId,
        sections: data.sections,
      })
    );

    btn.removeAttribute('data-added');
    revealCartLine(cartItems, data.key);
  } catch (err) {
    console.error('Upsell ATC error:', err);
    btn.removeAttribute('data-added');
  }
});

// Sync variant select → ATC button data-variant-id
document.addEventListener('change', (event) => {
  const select = /** @type {HTMLSelectElement|null} */ (event.target?.closest('.upsell-variant-select'));
  if (!select) return;

  const row = select.closest('.upsell-product-row');
  const btn = /** @type {HTMLElement|null} */ (row?.querySelector('.upsell-add-btn'));
  if (btn) btn.dataset.variantId = select.value;
});
